import { z } from "zod";

import { COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import { AutomationSignalSchema, type AutomationSignalType } from "../automation-signals";
import { CaseType } from "../case-core";
import { AutomationSubjectSchema } from "./signal-subject";

const internalStaff = COGNITO_INTERNAL_STAFF_GROUPS;

/** Signals the case lifecycle or a task policy reacts to; each gets an EventBridge rule. */
export const REACTOR_SIGNAL_TYPES = [
  "task.created",
  "task.assigned",
  "task.completed",
  "task.canceled",
  "task.reopened",
  "case.requirements_ready",
  "document.uploaded",
] as const satisfies readonly AutomationSignalType[];

/** Deliveries of one signal before its run is recorded as failed; the queue's `maxReceiveCount`. */
export const AUTOMATION_SIGNAL_MAX_RECEIVES = 5;

const AutomationRunActionSchema = z.object({
  source: z
    .string()
    .describe(
      "What produced the action: a case lifecycle event, or a policy (`task-assignment`, …)",
    ),
  type: z.string().describe("A `AutomationAction` type"),
  params: z.record(z.string(), z.unknown()),
  status: z.enum(["succeeded", "converged", "skipped", "failed", "not_run"]),
  code: z.string().nullable().describe("Skip reason or typed API error code"),
  error: z.string().nullable(),
  result: z.object({ taskId: z.string().optional() }).nullable(),
});
export type AutomationRunAction = z.infer<typeof AutomationRunActionSchema>;

const AutomationRunTransitionSchema = z.object({
  event: z.string(),
  from: z
    .string()
    .describe("Case lifecycle state before the event, e.g. `negotiation.first_contact`"),
  to: z.string(),
});
export type AutomationRunTransition = z.infer<typeof AutomationRunTransitionSchema>;

const AutomationRunStatusSchema = z.enum(["succeeded", "skipped", "failed"]);
export type AutomationRunStatus = z.infer<typeof AutomationRunStatusSchema>;

/** What automation did about one signal: the case lifecycle transitions it caused and every action. */
const AutomationRunSchema = z
  .object({
    id: z.string().describe("The signal id"),
    subject: AutomationSubjectSchema.nullable(),
    signal: AutomationSignalSchema,
    status: AutomationRunStatusSchema,
    transitions: z.array(AutomationRunTransitionSchema),
    actions: z.array(AutomationRunActionSchema),
    error: z.string().nullable().describe("Why the run failed outside its actions"),
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime(),
  })
  .meta({ visibleTo: internalStaff });
export type AutomationRun = z.infer<typeof AutomationRunSchema>;

export const ListAutomationRunsQuerySchema = z.object({
  status: AutomationRunStatusSchema.optional(),
  signalType: z.string().optional(),
  subjectId: z.string().optional(),
  before: z.iso.datetime().optional().describe("Cursor: runs started before this instant"),
});

/** A run as the admin pages read it, with its case's type so resolutions show that case type's labels. */
export const AdminAutomationRunSchema = AutomationRunSchema.extend({
  caseType: CaseType.nullable().describe("The subject case's type; null for other subjects"),
}).meta({ visibleTo: internalStaff });
export type AdminAutomationRun = z.infer<typeof AdminAutomationRunSchema>;

export const ListAutomationRunsResponseSchema = z.object({
  items: z.array(AdminAutomationRunSchema).meta({ visibleTo: internalStaff }),
  nextBefore: z.iso.datetime().nullable().meta({ visibleTo: internalStaff }),
});

export const SubjectAutomationRunsResponseSchema = z.object({
  runs: z.array(AutomationRunSchema).meta({ visibleTo: internalStaff }),
});
